import {
  DATETIME_CHAT_FORMAT,
  DATETIME_FE_FORMAT,
  DATETIME_FE_FORMAT_ACTIVITY,
  DATETIME_FE_FORMAT_FULL,
  DATETIME_FE_FORMAT_HYPHENATED,
  DATETIME_FE_FORMAT_SHORT,
  DATETIME_FORMAT,
  DATETIME_INPUT_FORMAT,
  DATETIME_RFC_FORMAT,
  DATE_FE_FORMAT,
  DATE_FE_FORMAT_FULL,
  DATE_FE_FORMAT_SHORT,
  DATE_FORMAT,
  DATE_INPUT_FORMAT,
  TIME_FORMAT,
  TIME_FORMAT_FULL,
} from '@/constants';
import dayjs, { ConfigType } from 'dayjs';

import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import { isSafari } from 'react-device-detect';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(relativeTime);
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(isBetween);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);

/* DAYJS CLASS
  Since DayJS does not have built-in support for its class, we need
  to create it outselves, based on the existing constructor and
  prototype.
*/
const Dayjs_proto = Object.getPrototypeOf(dayjs());
const Dayjs = Dayjs_proto.constructor;
Dayjs.prototype = Dayjs_proto;

/* DAYTS FORMAT ENUM
  This is a simple enum that contains the formats that are used
  throughout the application. This is useful for standardizing the
  formats that are used and making it easier to change them in the
  future.
*/
export enum DaytsFormatEnum {
  DEFAULT = DATETIME_RFC_FORMAT,
  DATE = DATE_FORMAT,
  DATE_INPUT = DATE_INPUT_FORMAT,
  DATE_FE = DATE_FE_FORMAT,
  DATE_FE_FULL = DATE_FE_FORMAT_FULL,
  DATE_FE_SHORT = DATE_FE_FORMAT_SHORT,
  TIME = TIME_FORMAT,
  TIME_FULL = TIME_FORMAT_FULL,
  DATETIME = DATETIME_FORMAT,
  DATETIME_INPUT = DATETIME_INPUT_FORMAT,
  DATETIME_FE_FULL = DATETIME_FE_FORMAT_FULL,
  DATETIME_CHAT = DATETIME_CHAT_FORMAT,
  DATETIME_RFC = DATETIME_RFC_FORMAT,
  DATETIME_FE = DATETIME_FE_FORMAT,
  DATETIME_FE_SHORT = DATETIME_FE_FORMAT_SHORT,
  DATETIME_FE_HYPHENATED = DATETIME_FE_FORMAT_HYPHENATED,
  DATETIME_FE_ACTIVITY = DATETIME_FE_FORMAT_ACTIVITY,
}
/* TIME ENUM
  This is a simple enum that contains the number of milliseconds in
  common time intervals. This is useful for doing calculations with
  time and making it easier to read.
*/
export enum Time {
  SECOND = 1000,
  MINUTE = 60 * SECOND,
  HOUR = 60 * MINUTE,
  DAY = 24 * HOUR,
  WEEK = 7 * DAY,
  YEAR = 365 * DAY,
}
/* MONTH ENUM
  This is a simple enum that contains the number of the month in the year. This is useful for doing calculations with months and making it
  easier to read. Note that the month number starts at 0 (January) and
  ends at 11 (December) to be consistent with the JavaScript Date API.
*/
export enum Month {
  JANUARY = 0,
  FEBRUARY = 1,
  MARCH = 2,
  APRIL = 3,
  MAY = 4,
  JUNE = 5,
  JULY = 6,
  AUGUST = 7,
  SEPTEMBER = 8,
  OCTOBER = 9,
  NOVEMBER = 10,
  DECEMBER = 11,
}
/* DAYTS CLASS
  This is a wrapper around the Dayjs class that allows us to
  extend the functionality of Dayjs without modifying the original
  class. This is useful for adding custom methods or properties and
  having control over the class itself that we wouldn't normally have.

  By using this across our project, we are free to change the library
  in the future to use something other than Dayjs if we ever need to.
*/
export class Dayts extends Dayjs {
  /* CONSTRUCTOR
    No special logic. Just a wrapper around the Dayjs constructor.
    This allows us to create a Day object with the same API as Dayjs,
    but gives us the freedom to extend the logic whenever needed and
    even change to a different library in the future.

    If needed, special logic can be added to the constructor to allow
    for different types of input, validation, etc...
  */
  constructor(input?: ConfigType | Dayts) {
    if (input instanceof Dayts) input = input.toString();
    // Safari date parsing polyfill.
    if (isSafari && typeof input === 'string') {
      // Normalize non-ISO date prefixes without changing ISO dates or timezone offsets.
      input = input.replace(/^(\d{1,2})-(\d{1,2})-(\d{2}|\d{4})(?=\s|$)/, '$1/$2/$3');
    }
    super({
      date: input,
      args: arguments,
    });
  }
  /* STATIC METHODS
    These are basic utility methods that can be used without an existing
    instance of the class. These should be used sparingly and only when
    needed. They are not part of the core functionality of the class.
  */
  // Standardize the formats that are used throughout the application.
  static Format: typeof DaytsFormatEnum = DaytsFormatEnum;
  // Check if the input is an instance of the Day class
  static isDayt(input: unknown): input is Dayts {
    return input instanceof Dayts;
  }
  // Generate a random date between two dates.
  static random(start: ConfigType, end: ConfigType): Dayts {
    const a = new Dayts(start).valueOf();
    const b = new Dayts(end).valueOf();
    return new Dayts(a + Math.random() * (b - a));
  }
  /* CUSTOM METHODS
    These are methods that are specific to the class instaces and are
    added alongside the existing Dayjs methods. This is where we can
    add custom logic that is specific to our application.

    Any new methods must follow the naming schema outlined in our
    standards, and should be clear enough to be self-explanatory.
    If a method is not clear enough, it should be renamed or removed.
  */
  // Get the value of the date in milliseconds.
  valueOf(): number {
    return this.$d.getTime();
  }
  // Format the date using the provided format or the default format.
  formatAs(dayFormat: DaytsFormatEnum = DaytsFormatEnum.DEFAULT): string {
    return this.format(dayFormat);
  }
  toLocal(): Dayts {
    return new Dayts(this.add(new Date().getTimezoneOffset() * -1, 'minutes'));
  }
  setDate(dateStr: string): Dayts {
    const date = new Dayts(dateStr);
    return new Dayts(this.set('year', date.get('year')).set('month', date.get('month')).set('date', date.get('date')));
  }
  setTime(timeStr: string): Dayts {
    // Support HH:mm:ss.SSS or HH:mm:ss or HH:mm
    const [h = '00', m = '00', s = '00'] = timeStr.split(':');
    let secondStr = s,
      msStr = '000';
    if (s && s.includes('.')) {
      [secondStr, msStr] = s.split('.');
      msStr = msStr.padEnd(3, '0').slice(0, 3);
    }
    const hour = parseInt(h, 10) || 0;
    const minute = parseInt(m, 10) || 0;
    const second = parseInt(secondStr, 10) || 0;
    const ms = parseInt(msStr, 10) || 0;
    return new Dayts(this.set('hour', hour).set('minute', minute).set('second', second).set('millisecond', ms));
  }
}
